import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { throwError, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeoutWith } from 'rxjs/operators';
import * as Globals from '@global/constants';
import { environment } from '@environments/environment';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Router } from '@angular/router';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthenticationService,
    private oidcSecurityService: OidcSecurityService,
    private router: Router
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.url.startsWith(environment.apiUrl)) {
      return next.handle(request);
    }

    return this.waitForToken().pipe(
      switchMap((token) => {
        if (token) {
          request = request.clone({
            setHeaders: {
              Authorization: `${Globals.AUTHORIZATION} ${token}`
            }
          });
        }

        return next.handle(request).pipe(
          catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              // /rolUser y /menus responden 401 también cuando el usuario está
              // autenticado pero sin rol en MARESEL (de baja o no registrado).
              // Eso no es una sesión caducada: es "no autorizado", y debe
              // resolverlo el guard/el header llevando a /unauthorized, no un
              // logout silencioso a /login (si se trata igual, la navegación a
              // /login gana la carrera y cancela el redirect a /unauthorized).
              const isRoleCheck = request.url.endsWith('/rolUser') || request.url.endsWith('/menus');
              if (!isRoleCheck) {
                console.warn('⚠️ Token expirado o inválido (401). Redirigiendo al login...');
                // Limpieza local, no logoff real: el end_session de Okta cae en
                // un bookmark ajeno cuando el post_logout_redirect_uri no está
                // registrado en la app Okta (ver /unauthorized "login()").
                this.authService.cleanupSessionStorage();
                this.router.navigate(['/login']);
              }
            }
            return throwError(error);
          })
        );
      })
    );
  }

  private waitForToken(): Observable<string> {
    return timer(0, 150).pipe(
      map(() => this.readToken()),
      filter((token): token is string => token.length > 0),
      take(1),
      // Okta puede tardar un poco más en dejar el token disponible tras callback
      timeoutWith(3000, of(''))
    );
  }

  private readToken(): string {
    return (
      this.oidcSecurityService.getAccessToken('OKTA') ||
      this.authService.currentTokenValue ||
      ''
    );
  }
}
